import React, { useEffect, useMemo, useState } from 'react';
import './HistoriqueModal.css';

// Charte FFTT 2025 : joueur A en bleu, joueur B en rouge
const COLOR_A = "#0069FF";
const COLOR_B = "#FF2D50";

// --- Logique de calcul (portée depuis arbitre.js du Livescoring, testée avec
// des scénarios de deuce) ---------------------------------------------------

function computeScoreHistoryPoints(scoreHistory) {
    const points = [];
    const setFinalScores = [];
    let x = 0;

    scoreHistory.forEach(setExchanges => {
        let a = 0, b = 0;
        points.push({x, a, b});

        (setExchanges || []).forEach(inc => {
            if (inc < 0) a++;
            else if (inc > 0) b++;
            x++;
            points.push({x, a, b});
        });

        setFinalScores.push({a, b});
    });

    return {points, setFinalScores};
}

function getMancheFirstServer(premierServeur, mancheIndex) {
    const mancheNumber = mancheIndex + 1;
    if (mancheNumber % 2 !== 0) return premierServeur;
    return (premierServeur === "JA") ? "JB" : "JA";
}

function computeServerForPoint(mancheFirstServer, pointA, pointB) {
    const total = pointA + pointB;
    const prolongation = (pointA >= 10 && pointB >= 10);
    const servedByFirst = prolongation
        ? (total % 2 !== 0)
        : (Math.floor(total / 2) % 2 === 0);
    return servedByFirst ? mancheFirstServer : (mancheFirstServer === "JA" ? "JB" : "JA");
}

function computeManchesStats(scoreHistory, premierServeur) {
    return scoreHistory.map((setExchanges, mancheIndex) => {
        const firstServer = getMancheFirstServer(premierServeur, mancheIndex);

        let a = 0, b = 0;
        let streakPlayer = null, streakLen = 0;
        const stats = {
            a: {total: 0, maxStreak: 0, setPoints: 0, serviceWon: 0},
            b: {total: 0, maxStreak: 0, setPoints: 0, serviceWon: 0}
        };

        (setExchanges || []).forEach(inc => {
            if (inc !== -1 && inc !== 1) return;

            if (a + 1 >= 11 && (a + 1 - b) >= 2) stats.a.setPoints++;
            if (b + 1 >= 11 && (b + 1 - a) >= 2) stats.b.setPoints++;

            const server = computeServerForPoint(firstServer, a, b);
            const winner = (inc < 0) ? "a" : "b";

            if (winner === "a") a++; else b++;
            if ((winner === "a" && server === "JA") || (winner === "b" && server === "JB")) {
                stats[winner].serviceWon++;
            }

            streakLen = (streakPlayer === winner) ? streakLen + 1 : 1;
            streakPlayer = winner;
            stats[winner].maxStreak = Math.max(stats[winner].maxStreak, streakLen);
        });

        stats.a.total = a;
        stats.b.total = b;
        return stats;
    });
}

function computeCumulativeStats(manchesStats) {
    const cumulative = {
        a: {total: 0, maxStreak: 0, setPoints: 0, serviceWon: 0},
        b: {total: 0, maxStreak: 0, setPoints: 0, serviceWon: 0}
    };

    manchesStats.forEach(stats => {
        ['a', 'b'].forEach(player => {
            cumulative[player].total += stats[player].total;
            cumulative[player].setPoints += stats[player].setPoints;
            cumulative[player].serviceWon += stats[player].serviceWon;
            cumulative[player].maxStreak = Math.max(cumulative[player].maxStreak, stats[player].maxStreak);
        });
    });

    return cumulative;
}

// --- Sous-composants d'affichage --------------------------------------------

// Même convention que CartridgePlayers (drapeaux dans /public/images/atlas_flags,
// avec repli sur l'image "pas de photo" si le code pays n'a pas de drapeau).
function PlayerFlag({countryCode}) {
    if (!countryCode) return null;
    return (
        <img
            className="historique_flag"
            src={process.env.PUBLIC_URL + "/images/atlas_flags/" + countryCode.toLowerCase() + "-flag.jpg"}
            alt={countryCode}
            onError={({currentTarget}) => {
                currentTarget.onerror = null;
                currentTarget.src = process.env.PUBLIC_URL + "/images/no-pictures.png";
            }}
        />
    );
}

function HistoriqueChart({exchanges, mancheNumber, nomA, nomB}) {
    const {points} = computeScoreHistoryPoints([exchanges || []]);

    if (points.length < 2) {
        return <p className="historique_empty">Aucun échange enregistré pour cette manche.</p>;
    }

    const width = 680;
    const height = 360;
    const padding = {top: 26, right: 20, bottom: 64, left: 36};
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;

    const maxX = points[points.length - 1].x || 1;
    const maxY = Math.max(11, ...points.map(p => Math.max(p.a, p.b))) + 1;

    const xScale = x => padding.left + (x / maxX) * plotWidth;
    const yScale = y => padding.top + plotHeight - (y / maxY) * plotHeight;

    const lineA = points.map(p => `${xScale(p.x)},${yScale(p.a)}`).join(" ");
    const lineB = points.map(p => `${xScale(p.x)},${yScale(p.b)}`).join(" ");

    const axisBottom = padding.top + plotHeight;

    const yTicks = [];
    for (let y = 0; y <= maxY; y += 1) yTicks.push(y);

    // Densité des graduations adaptative pour éviter la surcharge visuelle
    let xStep = 1;
    if (maxX > 20) xStep = 2;
    if (maxX > 40) xStep = 5;
    const xTicks = [];
    for (let x = xStep; x <= maxX; x += xStep) xTicks.push(x);

    const finalA = points[points.length - 1].a;
    const finalB = points[points.length - 1].b;

    return (
        <svg className="historique_svg" viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
            <text x={xScale(maxX / 2)} y={padding.top - 10} textAnchor="middle" fontSize="11" fill="#666">
                Manche {mancheNumber} : {finalA}-{finalB}
            </text>

            {yTicks.map(y => (
                <React.Fragment key={"y" + y}>
                    <line x1={padding.left} y1={yScale(y)} x2={width - padding.right} y2={yScale(y)} stroke="#eee" />
                    <text x={padding.left - 8} y={yScale(y) + 4} textAnchor="end" fontSize="11" fill="#999">{y}</text>
                </React.Fragment>
            ))}

            <line x1={padding.left} y1={axisBottom} x2={width - padding.right} y2={axisBottom} stroke="#333" />
            <line x1={padding.left} y1={padding.top} x2={padding.left} y2={axisBottom} stroke="#333" />

            {xTicks.map(x => (
                <React.Fragment key={"x" + x}>
                    <line x1={xScale(x)} y1={padding.top} x2={xScale(x)} y2={axisBottom} stroke="#eee" />
                    <line x1={xScale(x)} y1={axisBottom} x2={xScale(x)} y2={axisBottom + 4} stroke="#333" />
                    <text x={xScale(x)} y={axisBottom + 16} textAnchor="middle" fontSize="10" fill="#999">{x}</text>
                </React.Fragment>
            ))}

            <polyline points={lineA} fill="none" stroke={COLOR_A} strokeWidth="2" />
            <polyline points={lineB} fill="none" stroke={COLOR_B} strokeWidth="2" />

            <text x={(padding.left + width - padding.right) / 2} y={height - 34} textAnchor="middle" fontSize="11" fill="#999">
                Nombre d'échanges
            </text>
            <text x={padding.left} y={height - 12} fontSize="12" fill={COLOR_A}>{nomA} : {finalA}</text>
            <text x={width - padding.right} y={height - 12} textAnchor="end" fontSize="12" fill={COLOR_B}>{nomB} : {finalB}</text>
        </svg>
    );
}

function HistoriqueStatsTable({stats, nomA, nomB, countryCodeA, countryCodeB, leadingRow, extraRow}) {
    const rows = [
        ["Total score", stats.a.total, stats.b.total],
        ["Points consécutifs", stats.a.maxStreak, stats.b.maxStreak],
        ["Balles de set", stats.a.setPoints, stats.b.setPoints],
        ["Service gagnant", stats.a.serviceWon, stats.b.serviceWon],
    ];
    if (leadingRow) rows.unshift(leadingRow);
    if (extraRow) rows.push(extraRow);

    return (
        <table className="historique_stats_table">
            <thead>
                <tr>
                    <th></th>
                    <th className="historique_col_a"><PlayerFlag countryCode={countryCodeA} /> {nomA}</th>
                    <th className="historique_col_b"><PlayerFlag countryCode={countryCodeB} /> {nomB}</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(([label, valA, valB]) => (
                    <tr key={label}>
                        <td className="historique_stats_label">{label}</td>
                        <td>{valA}</td>
                        <td>{valB}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// --- Modale -------------------------------------------------------------

function HistoriqueModal({scoreHistory, premierServeur, nomA, nomB, countryCodeA, countryCodeB, cards, isFinished = false, onClose, onRefresh}) {
    const safeHistory = scoreHistory || [];
    // Le temps mort n'est pas horodaté par manche côté livescoring (un seul
    // indicateur "pris ou non" par joueur pour toute la partie) : on ne
    // l'affiche donc que dans la vue "Total manches".
    const timeoutRow = [
        "Temps mort",
        (cards?.timeOut?.[0] === "T") ? "Oui" : "Non",
        (cards?.timeOut?.[1] === "T") ? "Oui" : "Non",
    ];
    // Manches réellement jouées (au moins un échange) : une manche non
    // débutée peut apparaître dans score_history sous forme de tableau vide
    // (ex. 4e manche d'une partie gagnée 3-0) et ne doit pas être proposée.
    const playedMancheIndices = safeHistory
        .map((exchanges, i) => ((exchanges || []).length > 0 ? i : null))
        .filter(i => i !== null);

    // Par défaut, la dernière manche jouée est sélectionnée, sauf pour une
    // partie déjà terminée où on ouvre directement sur "Total manches". Ce
    // choix n'est recalculé qu'au montage : la modale n'étant rendue que
    // quand elle est ouverte, une nouvelle ouverture repart bien sur ce choix.
    const [selectedManche, setSelectedManche] = useState(() => (
        (!isFinished && playedMancheIndices.length > 0)
            ? playedMancheIndices[playedMancheIndices.length - 1]
            : null
    ));
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = async () => {
        if (!onRefresh) return;
        setRefreshing(true);
        try {
            await onRefresh();
        }
        finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        // On demande une donnée à jour au serveur dès l'ouverture, plutôt que
        // de se fier uniquement au cache local.
        handleRefresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const {setFinalScores, manchesStats, cumulativeStats, manchesWonRow} = useMemo(() => {
        const history = scoreHistory || [];
        const {setFinalScores} = computeScoreHistoryPoints(history);
        const manchesStats = computeManchesStats(history, premierServeur);
        const cumulativeStats = computeCumulativeStats(manchesStats);

        let manchesWonA = 0, manchesWonB = 0;
        setFinalScores.forEach(({a, b}) => {
            if (a > b) manchesWonA++;
            else if (b > a) manchesWonB++;
        });
        const manchesWonRow = ["Total manche", manchesWonA, manchesWonB];

        return {setFinalScores, manchesStats, cumulativeStats, manchesWonRow};
    }, [scoreHistory, premierServeur]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="historique_overlay" onClick={handleOverlayClick}>
            <div className="historique_box">
                <div className="historique_header">
                    <h2>Historique de la partie</h2>
                    <button type="button" className="historique_refresh_btn" onClick={handleRefresh} disabled={refreshing}>
                        {refreshing ? "Actualisation..." : "⟳ Actualiser"}
                    </button>
                    <button type="button" className="historique_close_btn" onClick={onClose} aria-label="Fermer">×</button>
                </div>

                {safeHistory.length === 0 ? (
                    <p className="historique_empty">Aucun historique disponible pour cette partie.</p>
                ) : (
                    <>
                        <div className="historique_manches_list">
                            <button
                                type="button"
                                className={"historique_manche_btn" + (selectedManche === null ? " historique_manche_btn--active" : "")}
                                onClick={() => setSelectedManche(null)}
                            >
                                Total manches
                            </button>
                            {playedMancheIndices.map((i) => (
                                <button
                                    type="button"
                                    key={i}
                                    className={"historique_manche_btn" + (selectedManche === i ? " historique_manche_btn--active" : "")}
                                    onClick={() => setSelectedManche(i)}
                                >
                                    Manche {i + 1} : {setFinalScores[i].a}-{setFinalScores[i].b}
                                </button>
                            ))}
                        </div>

                        <div className="historique_body">
                            {selectedManche === null ? (
                                <HistoriqueStatsTable
                                    stats={cumulativeStats}
                                    nomA={nomA}
                                    nomB={nomB}
                                    countryCodeA={countryCodeA}
                                    countryCodeB={countryCodeB}
                                    leadingRow={manchesWonRow}
                                    extraRow={timeoutRow}
                                />
                            ) : (
                                <>
                                    <HistoriqueChart
                                        exchanges={safeHistory[selectedManche]}
                                        mancheNumber={selectedManche + 1}
                                        nomA={nomA}
                                        nomB={nomB}
                                    />
                                    <HistoriqueStatsTable
                                        stats={manchesStats[selectedManche]}
                                        nomA={nomA}
                                        nomB={nomB}
                                        countryCodeA={countryCodeA}
                                        countryCodeB={countryCodeB}
                                    />
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default HistoriqueModal;
